import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon1 from "@assets/icons/Icon";
import SearchField from "@components/ui/textfields/SearchField";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { useDisponibiliteStore } from "@store/disponibiliteStore";
import { useAuthStore } from "@store/auth";
import { ordresApi } from "@services/api/ordres";
import type { Ordre, OrdreStatus } from "types/ordre";

// ─── Constants ──────────────────────────────────────────────

const COLORIS_TO_PAINT: Record<string, string> = {
  noir: "black", blanc: "white", gris: "grey", bleu: "blue",
  rouge: "red", vert: "green", jaune: "yellow", marron: "brown",
  orange: "orange", autre: "silver",
};

function getVehicleImageUrl(marque?: string, modele?: string, coloris?: string): string | null {
  if (!marque || !modele) return null;
  const make = encodeURIComponent(marque.toLowerCase());
  const model = encodeURIComponent(modele.toLowerCase());
  const paint = COLORIS_TO_PAINT[coloris || ""] || "grey";
  return `https://cdn.imagin.studio/getimage?customer=img&make=${make}&modelFamily=${model}&modelYear=2023&zoomType=fullscreen&angle=0&paintDescription=${paint}`;
}

const STATUS_DISPLAY: Partial<Record<OrdreStatus, { label: string; bg: string; color: string }>> = {
  a_preparer: { label: "en prépa", bg: "#F4F4F4", color: "#A1A1A1" },
  commande: { label: "commandé", bg: "#ECFFFE", color: "#47DDD5" },
  pret: { label: "prêt", bg: "#E8EAFF", color: "#5C6BFF" },
  reception: { label: "réception", bg: "#F4F4F4", color: "#A1A1A1" },
  en_cours: { label: "en cours", bg: "#CACFFF", color: "#5C6BFF" },
  devis: { label: "devis", bg: "#FEF3C7", color: "#D97706" },
  accord: { label: "accord", bg: "#D1FAE5", color: "#059669" },
  fin_de_travaux: { label: "terminé", bg: "#D1FAE5", color: "#059669" },
  facturation: { label: "facturation", bg: "#E8EAFF", color: "#6366f1" },
  disponible: { label: "disponible", bg: "#F1E7F9", color: "#8258FF" },
  restitue: { label: "restitué", bg: "#D1FAE5", color: "#059669" },
};

// ─── Component ──────────────────────────────────────────────

export default function MobileOrdresScreen() {
  const navigate = useNavigate();
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const logout = useAuthStore((s) => s.logout);
  const fetchData = useAtelierStore((s) => s.fetchData);
  const fetchBlocks = useDisponibiliteStore((s) => s.fetchBlocks);
  const jobs = useAtelierStore((s) => s.jobs);

  const [search, setSearch] = useState("");
  const [ordres, setOrdres] = useState<Ordre[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch atelier data to get jobs assigned to current user
  useEffect(() => {
    if (orgData?.id) {
      fetchData(orgData.id);
      fetchBlocks(orgData.id);
    }
  }, [orgData?.id]);

  // Get ordre IDs assigned to this mechanic from atelier jobs
  const myOrdreIds = jobs
    .filter((j) => j.workerId === userData?.id)
    .map((j) => j.ordre_id)
    .filter((id): id is string => !!id);
  const uniqueOrdreIds = Array.from(new Set(myOrdreIds));

  // Fetch ordres
  const loadOrdres = useCallback(async () => {
    if (!uniqueOrdreIds.length) {
      setOrdres([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const results = await Promise.all(uniqueOrdreIds.map((id) => ordresApi.get(id).catch(() => null)));
      setOrdres(results.filter((o): o is Ordre => o !== null));
    } catch {
      setOrdres([]);
    }
    setLoading(false);
  }, [uniqueOrdreIds.join(",")]);

  useEffect(() => {
    loadOrdres();
  }, [loadOrdres]);

  const filtered = search.trim()
    ? ordres.filter((o) => {
        const q = search.toLowerCase();
        return o.name.toLowerCase().includes(q) || o.num.toLowerCase().includes(q) || (o.immatriculation || "").toLowerCase().includes(q);
      })
    : ordres;

  return (
    <div className="flex flex-col h-screen bg-white" style={{ fontFamily: "Poppins" }}>
      {/* Header */}
      <div className="flex flex-row items-center justify-between" style={{ padding: "30px 20px 0" }}>
        <span style={{ fontWeight: 500, fontSize: 20, lineHeight: "30px", color: "#000000" }}>
          Ordres en cours
        </span>
        <button onClick={logout} className="flex items-center justify-center" style={{ padding: "0 10px" }}>
          <Icon1 name="logout" size={20} color="#A1A1A1" />
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: "20px 20px 0" }}>
        <SearchField value={search} onChange={setSearch} placeholder="Rechercher .." />
      </div>

      {/* Section label */}
      <div style={{ padding: "20px 20px 5px" }}>
        <span style={{ fontWeight: 500, fontSize: 13, lineHeight: "20px", color: "#A8A8A8" }}>
          Vos ordres assignés
        </span>
      </div>

      {/* Orders list */}
      <div className="flex-1 overflow-y-auto" style={{ padding: "0 20px 20px" }}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span style={{ fontSize: 13, color: "#A8A8A8" }}>Chargement...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <span style={{ fontSize: 13, color: "#A8A8A8" }}>
              {search ? "Aucun résultat" : "Aucun ordre assigné"}
            </span>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: 7 }}>
            {filtered.map((ordre) => {
              const status = STATUS_DISPLAY[ordre.status];
              const imgUrl = ordre.vehicle
                ? getVehicleImageUrl(ordre.vehicle.marque, ordre.vehicle.modele, ordre.vehicle.coloris)
                : null;
              return (
                <button
                  key={ordre.id}
                  onClick={() => navigate(`/m/ordre/${ordre.id}`)}
                  className="flex flex-row items-stretch text-left"
                  style={{
                    padding: 4, gap: 10,
                    background: "#FFFFFF",
                    boxShadow: "0px 2px 6.3px rgba(0, 0, 0, 0.15)",
                    borderRadius: 10,
                  }}
                >
                  {/* Vehicle thumbnail */}
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ width: 69, height: 64, background: "#F2F2F2", borderRadius: 10, overflow: "hidden" }}
                  >
                    {imgUrl ? (
                      <img src={imgUrl} alt="" style={{ width: 59, objectFit: "contain" }} />
                    ) : (
                      <Icon1 name="car" size={24} color="#A1A1A1" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex flex-col justify-center flex-1" style={{ padding: "4px 0", gap: 3, minWidth: 0 }}>
                    <span className="truncate" style={{ fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#1E1E1E" }}>
                      {ordre.name}
                    </span>
                    <div className="flex flex-col" style={{ gap: 2 }}>
                      <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>
                        {ordre.num}
                      </span>
                      <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>
                        {ordre.immatriculation || ordre.vehicle?.plate || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Status badge */}
                  {status && (
                    <div
                      className="flex items-center justify-center flex-shrink-0 self-end"
                      style={{
                        padding: "4px 10px",
                        background: status.bg,
                        borderRadius: 5,
                      }}
                    >
                      <span style={{ fontWeight: 500, fontSize: 12, lineHeight: "18px", color: status.color }}>
                        {status.label}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom indicator */}
      <div className="flex justify-center" style={{ padding: "10px 0 8px" }}>
        <div style={{ width: 104, height: 5, background: "#000000", borderRadius: 30 }} />
      </div>
    </div>
  );
}
